import * as MUTATIONS from './mutation-types.js';
import defaultState from './state';
import { captureError } from "@/services/userMonitoring";

let newState = {};
Object.assign(newState, defaultState);
export const companyModule = {
  state: newState,
  mutations: {
    [MUTATIONS.CLEAR]: (state) => { Object.assign(state, defaultState) },
    [MUTATIONS.ACCOUNT_BALANCE]: (state, value) => { state.ACCOUNT_BALANCE = value },
    [MUTATIONS.LEAD_ALERT_SETTINGS]: (state, value) => { state.LEAD_ALERT_SETTINGS = value },
    [MUTATIONS.COMPANY_USER]: (state, value) => { state.COMPANY_USER = value },
    [MUTATIONS.MAIN_LISTING]: (state, value) => { state.MAIN_LISTING = value },
    [MUTATIONS.COMPANY]: (state, value) => { state.COMPANY = value },
    [MUTATIONS.COMPANY_LOCALE]: (state, value) => { state.COMPANY_LOCALE = value },
  },
  getters: {
    showSettings: (state) => state.COMPANY?.account.show_settings ?? false
  },
  actions: {
    accountBalance: async function({commit}) {
      try {
        let result = await this._vm.$api.company.balance();
        commit(MUTATIONS.ACCOUNT_BALANCE, result.data.data.balance);
      } catch (e) {
        captureError(e, {
            section: "polls",
            action: "account-balance",
            type: "api-request",
        });
      }
    },
    leadAlertSettings: async function({commit}) {
      try {
        let result = await this._vm.$api.company.getLeadAlertSettings();
        commit(MUTATIONS.LEAD_ALERT_SETTINGS, result.data.data);
      } catch (e) {
        captureError(e, {
            section: "settings",
            action: "lead-alerts-get",
            type: "api-request",
        });
      }
    },
    updateLeadAlertSettings: async function({commit}, value) {
      try {
        await this._vm.$api.company.updateLeadAlertSettings(value);
        commit(MUTATIONS.LEAD_ALERT_SETTINGS, value);
        return true;
      } catch (e) {
        captureError(e, {
            section: "settings",
            action: "lead-alerts-update",
            type: "api-request",
        });
        return false;
      }
    },
    getCompanyUser: async function({commit}) {
      try {
        let result = await this._vm.$api.company.user();
        commit(MUTATIONS.COMPANY_USER, result.data.data[0]);
        return result.data.data[0];
      } catch (e) {
        captureError(e, {
          section: "company",
          action: "get-user",
          type: "api-request",
        });
      }
      return null;
    },
    updateCompanyUser: async function({commit, state}, {email, marketingPreference, invoiceEmail, invoiceEmailOnly}) {
      try {
        let result = await this._vm.$api.company.updateUser(email, marketingPreference, invoiceEmail, invoiceEmailOnly);
        commit(MUTATIONS.COMPANY_USER, {
          ...state.COMPANY_USER,
          email,
          receive_marketing_email: marketingPreference,
          invoiceEmail,
          invoice_email_only: invoiceEmailOnly
        });
        return { success: result.status === 202 };
      } catch (e) {
        captureError(e, {
            section: "company",
            action: "update-user",
            type: "api-request",
        });
        return { success:false, errors: e.response.data.errors };
      }
    },
    getListing: async function({commit}) {
      return this._vm.$api.company.getListing()
        .then(result => {
          let listing = result.data.data.listing;
          listing.description = (result.data.data.description) ? result.data.data.description.description : null;
          listing.images = result.data.data.images;
          commit(MUTATIONS.MAIN_LISTING, listing);
        });
    },
    saveListing: async function({commit}, listing) {
      try {
        let result = await this._vm.$api.company.saveListing(listing);
        // eslint-disable-next-line no-param-reassign
        listing = result.data.data.listing;
        listing.description = result.data.data.description.description;
        listing.images = result.data.data.images;
        commit(MUTATIONS.MAIN_LISTING, listing);
      } catch (e) {
        captureError(e, {
            section: "company",
            action: "save-listing",
            type: "api-request",
        });
      }
    },
    saveListingLogo: async function({commit}, data) {
      return this._vm.$api.company.saveListingLogo(data).then(result => {
        let listing = result.data.data.listing;
        listing.description = result.data.data.description.description;
        listing.images = result.data.data.images;
        commit(MUTATIONS.MAIN_LISTING, listing);
      }).catch(e => {
        captureError(e, {
            section: "company",
            action: "save-logo",
            type: "api-request",
        });
      });
    },
    saveListingImages: async function({commit}, data) {
      try {
        let result = await this._vm.$api.company.saveListingImages(data);
        let listing = result.data.data.listing;
        listing.description = result.data.data.description.description;
        listing.images = result.data.data.images;
        commit(MUTATIONS.MAIN_LISTING, listing);
      } catch (e) {
        captureError(e, {
            section: "company",
            action: "save-images",
            type: "api-request",
        });
      }
    },
    updateListingImage: async function({commit}, data) {
      try {
        let result = await this._vm.$api.company.updateListingImage(data);
        let listing = result.data.data.listing;
        listing.description = result.data.data.description.description;
        listing.images = result.data.data.images;
        commit(MUTATIONS.MAIN_LISTING, listing);
      } catch (e) {
        captureError(e, {
            section: "company",
            action: "update-images",
            type: "api-request",
        });
      }
    },
    getCompany: async function({commit}) {
      try {
        let result = await this._vm.$api.company.getCompany();
        commit(MUTATIONS.COMPANY, result.data.data.company);
        let languageCode = result.data.data.company.communications_language.iso_code;
        let countryCode = result.data.data.company.country.iso_code;

        commit(MUTATIONS.COMPANY_LOCALE, languageCode.toLowerCase() + '-' + countryCode.toUpperCase());
        return result.data.data.company
      } catch (e) {
        captureError(e, {
            section: "company",
            action: "get-company",
            type: "api-request",
        });
      }
    },
    completeRegistrationStep: async function({commit, state}, stepNumber) {
      const fields = {
        1: 'step_one_completed_at',
        2: 'step_two_completed_at',
        3: 'step_three_completed_at',
        4: 'step_four_completed_at',
      };
      await this._vm.$api.registrationJourney.completeStep(stepNumber);
      commit(MUTATIONS.COMPANY, {
        ...state.COMPANY,
        registration_journey: {
          ...state.COMPANY.registration_journey,
          [fields[stepNumber]]: new Date(),
        },
      });
    },
    updateCompany: async function({dispatch}, options) {
      try {
        await this._vm.$api.company.updateCompany(options);
        dispatch('getCompany');
        return true;
      } catch (e) {
        captureError(e, {
            section: "company",
            action: "update",
            type: "api-request",
        });
        return false;
      }
    },
  }
};
